/**
 * Routes Page - Date selector and route list
 *
 * Albert Heijn-style horizontal date picker with route cards.
 * Shows Routes__c for the logged-in driver (Chauffeur__c).
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { WbButton, WbChip, WbDateStrip, WbFactRow, WbState } from '../components/wb';
import { useHeader } from '../stores';
import clsx from 'clsx';
import { RoutePreviewMap } from '../components/route/RoutePreviewMap';
import { salesforceClient } from '../lib/salesforce';
import { metGetelde } from '../lib/utils';
import type { GeplandeRitWithDetails } from '../lib/salesforce';
import { useAuthStore } from '../stores/auth-store';

// Route with stops as returned from our Apex API
interface RouteFromApi {
  Id: string;
  Name: string;
  Datum__c: string;
  Routenaam_voor_Kalenderweergave__c?: string;
  Chauffeur__c?: string;
  Chauffeur__r?: { Id: string; Name: string };
  Geplande_Ritten__c?: number;
  Geplande_Ritten_Afleveren__c?: number;
  stops: GeplandeRitWithDetails[];
  totalStops: number;
  completedStops: number;
}

export function RoutesPage() {
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]!
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [routes, setRoutes] = useState<RouteFromApi[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Generate dates: 2 dagen verleden + vandaag + 14 dagen vooruit (17 totaal).
  // Geeft chauffeur 2 weken vooruit-zicht voor reparatie-stops, recurrente
  // routes en planning-checks.
  const PAST_DAYS = 2;
  const FUTURE_DAYS = 14;
  const TOTAL_DAYS = PAST_DAYS + 1 + FUTURE_DAYS;
  const dates = useMemo(() => Array.from({ length: TOTAL_DAYS }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + (i - PAST_DAYS));
    return {
      dateStr: date.toISOString().split('T')[0]!,
      dayName: new Intl.DateTimeFormat('nl-NL', { weekday: 'short' }).format(date),
      dayNum: date.getDate(),
      isToday: date.toDateString() === new Date().toDateString(),
    };
  }), []);

  // Fetch routes for the selected date
  const fetchRoutes = useCallback(async (date: string, showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const response = await salesforceClient.getDriverRoutes(date);
      console.log('[RoutesPage] API response:', response);

      if (response.success && response.data) {
        // The API returns an array of routes with inline stops
        setRoutes((response.data as unknown as RouteFromApi[]).map(metGetelde));
      } else {
        console.error('[RoutesPage] API error response:', response);
        setError(response.message || 'Kon routes niet ophalen');
        setRoutes([]);
      }
    } catch (err) {
      console.error('Error fetching routes:', err);
      // Auth errors are handled globally by onAuthError listener in App.tsx
      // Just show a generic error message here
      setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden van routes');
      setRoutes([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Fetch routes when date changes
  useEffect(() => {
    fetchRoutes(selectedDate);
  }, [selectedDate, fetchRoutes]);

  // Pull to refresh handler
  const handleRefresh = async () => {
    await fetchRoutes(selectedDate, true);
  };

  const formatDateLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (dateStr === today.toISOString().split('T')[0]) return 'Vandaag';
    if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Morgen';
    if (dateStr === yesterday.toISOString().split('T')[0]) return 'Gisteren';

    return new Intl.DateTimeFormat('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);
  };

  useHeader('Routes', formatDateLabel(selectedDate));

  return (
    <div className="space-y-4">
      <WbDateStrip
        dagen={dates.map((d) => ({ dateStr: d.dateStr, label: d.isToday ? 'Vandaag' : d.dayName, dayNum: d.dayNum, isToday: d.isToday }))}
        value={selectedDate}
        onChange={setSelectedDate}
      />

      {isRefreshing && <p className="font-mono text-sm text-wb-inkt2 text-center">Verversen…</p>}
      {isLoading && <WbState variant="laden" titel="Routes laden…" />}
      {!isLoading && error && (
        <WbState variant="fout" titel="Kon routes niet laden" tekst={error} onOpnieuw={handleRefresh}>
          <WbButton variant="link" onClick={() => { useAuthStore.getState().logout(); navigate('/login'); }}>Of log opnieuw in</WbButton>
        </WbState>
      )}
      {!isLoading && !error && routes.length === 0 && (
        <WbState variant="leeg" titel="Geen routes." tekst={`Er zijn geen routes gepland voor ${formatDateLabel(selectedDate).toLowerCase()}.`} />
      )}

      {!isLoading && !error && routes.map((route) => {
        const isPickup = route.Routenaam_voor_Kalenderweergave__c?.toLowerCase().includes('ophalen');
        const klaar = route.completedStops === route.totalStops && route.totalStops > 0;
        const nextStop = route.stops?.find((s) => s.Status__c === 'Ingepland');
        const naam = route.Routenaam_voor_Kalenderweergave__c || route.Name;

        return (
          <section key={route.Id} className="rounded-wb-lg border-wb border-wb-rand bg-wb-vlak overflow-hidden">
            {/* Routekop: naam + type-vak (geel = ophalen, groen = afleveren) */}
            <div className="flex items-stretch border-b-wb border-wb-rand">
              <div className="flex-1 min-w-0 px-3 py-2">
                <p className="font-wb-heading font-bold text-xl leading-tight text-wb-inkt truncate">{naam}</p>
                <p className="font-mono text-sm text-wb-inkt2">
                  {route.completedStops}/{route.totalStops} stops{klaar ? ' · klaar' : ''}
                </p>
              </div>
              <div className={clsx('shrink-0 flex items-center px-3 font-wb-heading font-bold text-sm uppercase tracking-wide border-l-wb border-wb-rand text-wb-inkt', isPickup ? 'bg-wb-ophalen' : 'bg-wb-afleveren')}>
                {isPickup ? 'Ophalen' : 'Afleveren'}
              </div>
            </div>

            <div className="p-3 space-y-3">
              {nextStop && (
                <WbFactRow
                  facts={[
                    { label: 'Volgende', value: nextStop.customerName || 'Klant', tekst: true },
                    { label: 'Tijdvenster', value: nextStop.timeWindow ?? '–' },
                  ]}
                />
              )}
              {nextStop?.address && (
                <p className="text-sm text-wb-inkt2 whitespace-pre-line -mt-1">{nextStop.address.replace(/<br\s*\/?>/gi, '\n')}</p>
              )}
              {klaar && <WbChip kind="klaar">Voltooid</WbChip>}

              <WbButton size="lg" variant={klaar ? 'secondary' : 'primary'} onClick={() => navigate(`/routes/${route.Id}`)}>
                {route.completedStops === 0 ? 'Start route' : klaar ? 'Route bekijken' : 'Doorgaan'}
              </WbButton>

              {route.stops && route.stops.length > 0 && <RoutePreviewMap stops={route.stops} height={240} />}
            </div>
          </section>
        );
      })}

      {!isLoading && (
        <div className="flex justify-center">
          <WbButton variant="link" onClick={handleRefresh} disabled={isRefreshing}>Vernieuw routes</WbButton>
        </div>
      )}
    </div>
  );
}
